import { AfterViewInit, Component, ViewChild, ChangeDetectionStrategy, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { ValidationProblem, Order, OrderManagerService, ValidationService, SlideDownAnimation, AddressFormComponent, instantValidationThrottle } from 'reg-hub-common';
import { BehaviorSubject, takeUntil, timer } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-land-title-search-criteria',
  templateUrl: './land-title-search-criteria.component.html',
  styleUrl: './land-title-search-criteria.component.css',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [ SlideDownAnimation ]
})
export class LandTitleSearchCriteriaComponent extends SteppedComponent implements AfterViewInit {
  private readonly transloco = inject(TranslocoService);
  @ViewChild(AddressFormComponent, { static: false }) addressFormComponent!: AddressFormComponent;

  protected criteria!: FormGroup;

  protected searchByTitleNumber = false;
  protected searchByLegalDescription = false;
  protected searchByAddress = false;

  protected titleNumberErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected legalSubdivisionErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected quarterErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected sectionErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected townshipErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected rangeErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected meridianErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected extensionErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected planErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected blockOrUnitErrors$ = new BehaviorSubject<string | null | undefined>(null);
  protected lotErrors$ = new BehaviorSubject<string | null | undefined>(null);

  protected firstSegmentPlaceholder = '';
  protected firstSegmentTitle = '';
  protected secondSegmentPlaceholder = '';
  protected secondSegmentTitle = '';
  protected thirdSegmentPlaceholder = '';

  constructor(
    orderManager: OrderManagerService,
    validationService: ValidationService,
    private formBuilder: FormBuilder) { super(orderManager, validationService); }

  public override getValidationState(errors: ValidationProblem[] | undefined, order: Order): StepValidationState {
    return StepValidationState.ValidationSuccess;
  }

  public ngAfterViewInit(): void {
    this.onAddressValueChange();
  }

  protected override init(order: Order): void {
    super.init(order);

    this.criteria = this.formBuilder.group({
      titleNumber: this.order.landTitleSearchCriteria?.titleNumber,
      titleNumber1: null,
      titleNumber2: null,
      titleNumber3: null,
      legalDescriptionTypeID: this.order.landTitleSearchCriteria?.legalDescriptionTypeID,
      legalSubdivision: this.order.landTitleSearchCriteria?.legalSubdivision,
      quarter: this.order.landTitleSearchCriteria?.quarter,
      section: this.order.landTitleSearchCriteria?.section,
      township: this.order.landTitleSearchCriteria?.township,
      range: this.order.landTitleSearchCriteria?.range,
      meridian: this.order.landTitleSearchCriteria?.meridian,
      extension: this.order.landTitleSearchCriteria?.extension,
      plan: this.order.landTitleSearchCriteria?.plan,
      blockOrUnit: this.order.landTitleSearchCriteria?.blockOrUnit,
      lot: this.order.landTitleSearchCriteria?.lot
    });

    this.initTitleNumber();
    this.initLegalDescription();
    this.searchByAddress = !!this.order.landTitleSearchCriteria?.address;

    this.observableErrors$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(errors => this.pushErrors(errors));
    this.criteria.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        instantValidationThrottle())
      .subscribe(() => this.validate(this.getOrderForValidation()));
  }

  private initTitleNumber(): void {
    this.searchByTitleNumber = !!this.order.landTitleSearchCriteria?.titleNumber;

    if (this.uiConfiguration.landTitleSearchCriteriaConfiguration?.titleNumberFirstSegmentMaxLength) {
      this.firstSegmentTitle = this.transloco.translate('orders.titleNumber2');
      for (let i = 1; i <= this.uiConfiguration.landTitleSearchCriteriaConfiguration.titleNumberFirstSegmentMaxLength; i++) {
        this.firstSegmentPlaceholder += i % 10;
      }
    }

    if (this.uiConfiguration.landTitleSearchCriteriaConfiguration?.titleNumberSecondSegmentMaxLength) {
      this.firstSegmentTitle = this.transloco.translate('orders.titleSegment');
      this.secondSegmentTitle = this.transloco.translate('orders.numberSegment');
      for (let i = 1; i <= this.uiConfiguration.landTitleSearchCriteriaConfiguration.titleNumberSecondSegmentMaxLength; i++) {
        this.secondSegmentPlaceholder += i % 10;
      }
    }

    if (this.uiConfiguration.landTitleSearchCriteriaConfiguration?.titleNumberThirdSegmentMaxLength) {
      for (let i = 1; i <= this.uiConfiguration.landTitleSearchCriteriaConfiguration.titleNumberThirdSegmentMaxLength; i++) {
        this.thirdSegmentPlaceholder += i % 10;
      }
    }

    const titleNumberControl = this.criteria.get('titleNumber');
    const titleNumber1Control = this.criteria.get('titleNumber1');
    const titleNumber2Control = this.criteria.get('titleNumber2');
    const titleNumber3Control = this.criteria.get('titleNumber3');

    const separator = this.uiConfiguration.landTitleSearchCriteriaConfiguration?.titleNumberSegmentSeparator;
    let titleNumber: (string | undefined)[];

    if(separator) {
      titleNumber = this.order.landTitleSearchCriteria?.titleNumber?.split(separator) ?? [ this.order.landTitleSearchCriteria?.titleNumber ];
    } else {
      titleNumber = [ this.order.landTitleSearchCriteria?.titleNumber ];
    }

    if (this.uiConfiguration.landTitleSearchCriteriaConfiguration?.titleNumberFirstSegmentMaxLength) {
      titleNumber1Control?.setValue(titleNumber?.at(0), { emitEvent: false });
    }

    if (this.uiConfiguration.landTitleSearchCriteriaConfiguration?.titleNumberSecondSegmentMaxLength) {
      titleNumber2Control?.setValue(titleNumber?.at(1), { emitEvent: false });
    }

    if (this.uiConfiguration.landTitleSearchCriteriaConfiguration?.titleNumberThirdSegmentMaxLength) {
      titleNumber3Control?.setValue(titleNumber?.at(2), { emitEvent: false });
    }

    titleNumber1Control?.valueChanges.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe(val => titleNumberControl?.setValue(this.trimByRegex(`${val ?? ''}${separator ?? ''}${titleNumber2Control?.value ?? ''}${separator ?? ''}${titleNumber3Control?.value ?? ''}`, separator), { emitEvent: false }));
    titleNumber2Control?.valueChanges.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(val => titleNumberControl?.setValue(this.trimByRegex(`${titleNumber1Control?.value ?? ''}${separator ?? ''}${val ?? ''}${separator ?? ''}${titleNumber3Control?.value ?? ''}`, separator), { emitEvent: false }));
    titleNumber3Control?.valueChanges.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(val => titleNumberControl?.setValue(this.trimByRegex(`${titleNumber1Control?.value ?? ''}${separator ?? ''}${titleNumber2Control?.value ?? ''}${separator ?? ''}${val ?? ''}`, separator), { emitEvent: false }));
  }

  private initLegalDescription(): void {
    const legalDescriptionTypeValue = this.criteria.get('legalDescriptionTypeID')?.value;
    this.searchByLegalDescription = legalDescriptionTypeValue !== null && legalDescriptionTypeValue !== undefined; 
  }

  public override pushErrors(errors: ValidationProblem[] | undefined): void {
    if (!errors) {
      return;
    }

    this.addressFormComponent?.pushErrors(errors);

    this.titleNumberErrors$.next(errors?.filter(error => error.path.includes('/titlenumber'))?.at(0)?.userFriendlyMessage);
    this.legalSubdivisionErrors$.next(errors?.filter(error => error.path.includes('/legalsubdivision'))?.at(0)?.userFriendlyMessage);
    this.quarterErrors$.next(errors?.filter(error => error.path.includes('/quarter'))?.at(0)?.userFriendlyMessage);
    this.sectionErrors$.next(errors?.filter(error => error.path.includes('/section'))?.at(0)?.userFriendlyMessage);
    this.townshipErrors$.next(errors?.filter(error => error.path.includes('/township'))?.at(0)?.userFriendlyMessage);
    this.rangeErrors$.next(errors?.filter(error => error.path.includes('/range'))?.at(0)?.userFriendlyMessage);
    this.meridianErrors$.next(errors?.filter(error => error.path.includes('/meridian'))?.at(0)?.userFriendlyMessage);
    this.extensionErrors$.next(errors?.filter(error => error.path.includes('/extension'))?.at(0)?.userFriendlyMessage);
    this.planErrors$.next(errors?.filter(error => error.path.includes('/plan'))?.at(0)?.userFriendlyMessage);
    this.blockOrUnitErrors$.next(errors?.filter(error => error.path.includes('/blockorunit'))?.at(0)?.userFriendlyMessage);
    this.lotErrors$.next(errors?.filter(error => error.path.includes('/lot'))?.at(0)?.userFriendlyMessage);
  }

  public override onSaving(): void {
    this.order.landTitleSearchCriteria = { ...this.criteria.value, party: this.order.landTitleSearchCriteria?.party };
    const address = this.criteria.get('address')?.getRawValue();
    
    if (address) {
      this.order.landTitleSearchCriteria!.address = address;
    }

    this.formatOrder(this.order);
  }

  private getOrderForValidation() {
    const order = this.copyOrder(this.order);
    order.landTitleSearchCriteria = { ...this.criteria.value, party: this.order.landTitleSearchCriteria?.party };

    const address = this.criteria.get('address')?.getRawValue();
    
    if (address) {
      order.landTitleSearchCriteria!.address = address;
    }

    this.formatOrder(order);

    return order;
  }

  private formatOrder(order: Order) {
    delete ((order.landTitleSearchCriteria! as any)['titleNumber1']);
    delete ((order.landTitleSearchCriteria! as any)['titleNumber2']);
    delete ((order.landTitleSearchCriteria! as any)['titleNumber3']);
    delete ((order.landTitleSearchCriteria! as any)['district']);
  }

  private trimByRegex(value: string, pattern: string | null | undefined): string {
    if (!pattern) {
      return value;
    }

    const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`^${escaped}+|${escaped}+$`, 'g');
    return value.replace(regex, '');
  }
  
  onTitleNumberValueChange(val: any) {
    if (val.checked === false) {
      this.criteria.get('titleNumber')?.setValue(null, { emitEvent: false });
    }
  }

  protected onLegalDescriptionValueChange() {
    const legalDescriptionTypeControl = this.criteria.get('legalDescriptionTypeID');

    if (this.searchByLegalDescription === true) {
      if (this.uiConfiguration.landTitleSearchCriteriaConfiguration?.legalDescriptionUIConfiguration?.showRuralLegalDescription === true &&
        this.uiConfiguration.landTitleSearchCriteriaConfiguration?.legalDescriptionUIConfiguration?.showUrbanLegalDescription === false) {
          legalDescriptionTypeControl?.setValue('Rural', { emitEvent: false });
      } else if (this.uiConfiguration.landTitleSearchCriteriaConfiguration?.legalDescriptionUIConfiguration?.showRuralLegalDescription === false &&
        this.uiConfiguration.landTitleSearchCriteriaConfiguration?.legalDescriptionUIConfiguration?.showUrbanLegalDescription === true) {
          legalDescriptionTypeControl?.setValue('Urban', { emitEvent: false });
      }
    } else  {
      // was causing some ugly clipping to have it disappear right away while sliding up
      timer(0.4).subscribe(() => this.criteria.get('legalDescriptionTypeID')?.setValue(null, { emitEvent: false }));
    }

    const legalDescriptionType = legalDescriptionTypeControl?.value;

    if (legalDescriptionType === 'Rural') {
      this.clearUrbanFields();
    } else if(legalDescriptionType === 'Urban') {
      this.clearRuralFields();
    } else {
      this.clearUrbanFields();
      this.clearRuralFields();
    }
  }

  protected onAddressValueChange() {
    if (this.searchByAddress) {
      this.addressFormComponent.address = this.order.landTitleSearchCriteria?.address;

      this.addressFormComponent.initForm();
      this.addressFormComponent.initJurisdictionsForm();

      this.criteria.setControl('address', this.addressFormComponent.addressDetails, { emitEvent: false });
    } else {
      this.criteria.setControl('address', null, { emitEvent: false });
    }
  }

  private clearRuralFields() {
    this.criteria.get('legalSubdivision')?.setValue(undefined, { emitEvent: false });
    this.criteria.get('quarter')?.setValue(undefined, { emitEvent: false });
    this.criteria.get('section')?.setValue(undefined, { emitEvent: false });
    this.criteria.get('township')?.setValue(undefined, { emitEvent: false });
    this.criteria.get('range')?.setValue(undefined, { emitEvent: false });
    this.criteria.get('meridian')?.setValue(undefined, { emitEvent: false });
    this.criteria.get('extension')?.setValue(undefined, { emitEvent: false });
  }

  private clearUrbanFields() {
    this.criteria.get('plan')?.setValue(undefined, { emitEvent: false });
    this.criteria.get('blockOrUnit')?.setValue(undefined, { emitEvent: false });
    this.criteria.get('lot')?.setValue(undefined, { emitEvent: false });
  }
}
